/**
 * Control Portal - Tenant User Management
 * Create/manage users with roles and location scopes
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Plus, Edit2, Trash2, Loader2,
  Shield, MapPin, Search, Key, RefreshCw, UserPlus
} from 'lucide-react';

export default function TenantUsersPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role_id: '',
    scope_location_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantRes, usersRes, rolesRes, locationsRes] = await Promise.all([
        controlApi.getTenant(tenantId),
        controlApi.getUsers(tenantId, { limit: 500 }),
        controlApi.getRoles(tenantId),
        controlApi.getLocations(tenantId, { limit: 500 })
      ]);
      setTenant(tenantRes.data);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateUserDialog = () => {
    setEditingUser(null);
    setUserForm({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      role_id: '',
      scope_location_id: ''
    });
    setUserDialogOpen(true);
  };

  const openEditUserDialog = (user) => {
    setEditingUser(user);
    setUserForm({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role_id: '',
      scope_location_id: ''
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.full_name || !userForm.email) {
      toast.error('Name and email are required');
      return;
    }

    if (!editingUser && !userForm.password) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        full_name: userForm.full_name,
        email: userForm.email,
        phone: userForm.phone || null,
        ...(userForm.password && { password: userForm.password })
      };

      let userId;
      if (editingUser) {
        await controlApi.updateUser(tenantId, editingUser.id, payload);
        userId = editingUser.id;
        toast.success('User updated');
      } else {
        const res = await controlApi.createUser(tenantId, payload);
        userId = res.data.id;
        toast.success('User created');
      }

      // Assign role if selected
      if (userForm.role_id && userId) {
        try {
          await controlApi.assignUserRoles(tenantId, userId, [{
            role_id: userForm.role_id,
            scope_location_id: userForm.scope_location_id || null
          }]);
          toast.success('Role assigned');
        } catch (error) {
            // Silently handled
        }
      }

      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Deactivate "${user.full_name}"?`)) return;

    try {
      await controlApi.updateUser(tenantId, user.id, { status: 'inactive' });
      toast.success('User deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  // Get location name by ID
  const getLocationName = (id) => {
    if (!id) return 'Tenant-wide';
    const loc = locations.find(l => l.id === id);
    return loc?.name || 'Unknown';
  };

  // Filter users by search
  const filteredUsers = users.filter(u =>
    u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tenant-users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/control/tenants/${tenantId}`)}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">User Management</h1>
            <p className="text-slate-400">{tenant?.name} - {users.length} users</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-slate-700 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={openCreateUserDialog}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.status === 'active').length}</p>
              <p className="text-slate-400 text-sm">Active Users</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
              <Shield className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{roles.length}</p>
              <p className="text-slate-400 text-sm">Roles Defined</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
              <MapPin className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{locations.length}</p>
              <p className="text-slate-400 text-sm">Locations</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-700">
              <Users className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.filter(u => u.status === 'inactive').length}</p>
              <p className="text-slate-400 text-sm">Inactive Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users..."
          className="pl-10 bg-slate-900 border-slate-700 text-white"
          data-testid="search-users"
        />
      </div>

      {/* Users Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-slate-800/50">
              <TableHead className="text-slate-400">User</TableHead>
              <TableHead className="text-slate-400">Email</TableHead>
              <TableHead className="text-slate-400">Phone</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Created</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow
                  key={user.id}
                  className="border-slate-800 hover:bg-slate-800/50"
                  data-testid={`user-row-${user.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <p className="text-white font-medium">{user.full_name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{user.email}</TableCell>
                  <TableCell className="text-slate-300">{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditUserDialog(user)}
                        className="h-8 w-8 text-slate-400 hover:text-blue-400"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(user)}
                        className="h-8 w-8 text-slate-400 hover:text-red-400"
                        disabled={user.status === 'inactive'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Roles Section */}
      <div className="mt-6 bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-400" />
            Available Roles
          </h3>
          <Button
            size="sm"
            onClick={() => navigate(`/control/tenants/${tenantId}/roles`)}
            className="bg-slate-800 hover:bg-slate-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Manage Roles
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {roles.length === 0 ? (
            <p className="text-slate-500">No roles defined. Create roles to assign to users.</p>
          ) : (
            roles.map(role => (
              <Badge
                key={role.id}
                variant="outline"
                className="border-slate-600 py-1 px-3"
              >
                <Shield className="w-3 h-3 mr-1" />
                {role.name}
              </Badge>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUser ? 'Edit User' : 'Create User'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingUser ? 'Update user information and role assignment' : 'Add a new user to this tenant'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Full Name *</Label>
              <Input
                value={userForm.full_name}
                onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                placeholder="John Doe"
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="user-name-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="user@company.com"
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="user-email-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">
                Password {editingUser ? '(leave empty to keep current)' : '*'}
              </Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="••••••••"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            {roles.length > 0 && (
              <>
                <div>
                  <Label className="text-slate-300">Assign Role</Label>
                  <Select
                    value={userForm.role_id}
                    onValueChange={(value) => setUserForm({ ...userForm, role_id: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue placeholder="Select a role (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id} className="text-white">
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {userForm.role_id && locations.length > 0 && (
                  <div>
                    <Label className="text-slate-300">Location Scope</Label>
                    <Select
                      value={userForm.scope_location_id}
                      onValueChange={(value) => setUserForm({ ...userForm, scope_location_id: value })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                        <SelectValue placeholder="Tenant-wide (all locations)" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="" className="text-white">
                          Tenant-wide (all locations)
                        </SelectItem>
                        {locations.filter(l => l.status === 'active').map(loc => (
                          <SelectItem key={loc.id} value={loc.id} className="text-white">
                            {loc.name} ({loc.type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-slate-500 text-xs mt-1">
                      User will only have access to this location and its children
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setUserDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSaveUser}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-user-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
